<template>
  <div>
    <vxe-grid
      ref="table"
      auto-resize
      highlight-hover-row
      :toolbar-config="tableToolbar"
      size="mini"
      :data="tableData"
      :columns="tableColumns"
      :edit-config="{ enabled: !disabled, trigger: 'click', mode: 'row' }"
      :loading="loading"
      :edit-rules="validRules"
      @edit-closed="saveData"
      cell-class-name="align-top"
      :footer-method="() => footerData"
      show-footer
    >
      <template v-slot:toolbar_form>
        <slot name="header"></slot>
      </template>
      <template v-slot:opt="{ row }">
        <div style="text-align: center">
          <el-dropdown trigger="click" @command="optCommand">
            <span class="el-dropdown-link">
              {{ $l('table_label_action') || '功能' }}
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="(opt, index) in tableOpts"
                :key="index"
                :icon="opt.icon"
                :command="{ opt, data: row }"
                :divided="opt.divided"
                :disabled="
                  opt.disabled == undefined ? false : opt.disabled(row)
                "
              >
                {{ opt.text }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </template>
      <template v-slot:displayImage="{ row: { defaultImageUrl } }">
        <el-image
          :src="defaultImageUrl || '/image/static/img/nopic.jpg'"
          :preview-src-list="[defaultImageUrl || '/image/static/img/nopic.jpg']"
          style="width: 100px; height: 66px"
          fit="cover"
        >
          <div slot="error" class="image-slot">
            <img
              src="/image/static/img/nopic.jpg"
              style="width: 100px; height: 66px"
            />
          </div>
        </el-image>
      </template>
      <template v-slot:sampleName="{ row }">
        <span>{{ row.devTrackingNo }}</span>
        <br v-if="row.devTrackingNo" />
        <span>{{ row.sampleStyleNo }}</span>
        <br />
        <span>{{ row.sampleSpecName }}</span>
        <br />
        <span>{{ row.sampleSpecNameEn }}</span>
      </template>
      <template v-slot:colorSize="{ row }">
        <span>{{ row.goodsSizeSpec }}</span>
        <br />
        <span>{{ row.colorName }}</span>
        <br />
        <span>{{ row.colorNameEn }}</span>
      </template>
      <template v-slot:empty="{}">
        <div style="text-align: center">
          <img src="/image/static/img/nodata.png" />
          <p class="no-data">
            {{ $l('tips_no_data') || '暂无数据！' }}
          </p>
        </div>
      </template>
    </vxe-grid>
  </div>
</template>
<script>
import TableAutoHeight from '@/mixins/table-auto-height'
import EditLoading from '@/mixins/edit-loading'
import tableColumns from './table-columns'
import tableOpts from './table-opts'
import { add, format } from '@/libs/util.number.js'
export default {
  mixins: [TableAutoHeight, EditLoading],
  props: {
    tableData: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tableToolbar: {
        slots: { buttons: 'toolbar_form' },
        refresh: { query: () => this.$emit('reload') },
        zoom: true,
        custom: true,
      },
      format: (number) => format(number),
      tableColumns: tableColumns(this),
      validRules: {
        qty: [
          {
            type: 'number',
            required: true,
            trigger: 'blur',
            pattern: /([1-9]\d*(\.\d*[1-9])?)|(0\.\d*[1-9])/,
            message:
              this.$l('tips_sample_must_be_greater_than_0') ||
              '样品数量须大于0',
          },
        ],
      },
    }
  },
  computed: {
    tableOpts() {
      const opts = tableOpts(this)
      return opts.filter(({ allow }) => (!allow ? true : this.allow(allow)))
    },
    footerData() {
      const totalColumnFields = this.tableColumns
        .filter((c) => c.isTotalColumn)
        .map((c) => c.field)

      const totalColumnValues = this.tableData.reduce((result, next) => {
        totalColumnFields.forEach((f) => {
          result[f] = add(result[f], next[f])
        })
        return result
      }, Object.fromEntries(totalColumnFields.map((f) => [f, 0])))

      const result = this.tableColumns
        .filter((c) => c.visible || c.visible === undefined)
        .map((t) => {
          const val = totalColumnValues[t.field]
          return val ? format(val) : ''
        })
      result[1] = this.$l('table_label_total') || '合计'
      return [result]
    },
  },
  methods: {
    optCommand({ opt, data }) {
      const { menuType } = opt
      if (!menuType) return
      switch (menuType) {
        case 'delete':
          this.del(data)
          break
        default:
          console.error('menuType configuration error:', menuType)
      }
    },
    async del({ sendSampleDetialCode }) {
      const confirm = await this.$confirm(
        this.$l('tips_delete') || '是否要删除该记录？',
        this.$l('label_completeDesc') || '确认信息',
        {
          confirmButtonText: this.$l('btn_confirm') || '确定',
          cancelButtonText: this.$l('btn_cancel') || '取消',
          type: 'warning',
        }
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return
      if (this.loadingOpen()) return

      const res = await this.$api
        .customerSendSampleDetailDelApi({ sendSampleDetialCode })
        .catch(() => null)
      this.loadingClose()
      if (!res) return

      const { status } = res
      if (status !== 200) {
        this.$message.error(this.$l('tips_delete_no') || '删除失败！')
        return
      }
      this.$message({
        message: this.$l('tips_delete_ok') || '删除成功！',
        type: 'success',
      })
      this.$emit('reload')
    },
    async validate() {
      const result = await this.$refs.table.validate(true).catch(() => true)
      return !result
    },
    async saveData({ row = {} }) {
      if (!(await this.validate())) return
      const index = this.tableData.findIndex(
        (data) => data.sendSampleDetialCode === row.sendSampleDetialCode
      )
      if (index < 0) return
      if (this.loadingOpen()) return
      const res = await this.$api
        .customerSendSampleDetailSaveApi({
          ...row,
        })
        .catch(() => null)
      this.loadingClose()
      if (!res) return

      const { status } = res

      if (status != 200) {
        this.$message.error(this.$l('tips_save_no') || '保存失败！')
        return
      }
      this.$message({
        showClose: true,
        message: this.$l('tips_save_ok') || '保存成功',
        type: 'success',
      })
      this.$emit('reload')
      this.$refs.table.updateFooter()
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep {
  .col--actived {
    padding: 7px 0 !important;
  }
}
.text {
  color: #6c6c6c;
  font-size: 14px;
}
</style>
